import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  createLabeledPathWithI18n,
  type PathGroupKey as PathGroupKeyBase,
  pathGroupLabelI18nKeyMap,
  type PathKey as PathKeyBase,
  useMatchedPathKey,
} from '~/apps/applicationAdmin/routes/paths'
import {
  type IconName,
  type NavigationGroup,
  type NavigationGroupItem,
} from '~/utils'

const pathGroupKeys = ['settings'] as const satisfies PathGroupKeyBase[]
type PathGroupKey = (typeof pathGroupKeys)[number]

const iconMap: Record<PathGroupKey, IconName> = {
  settings: 'gears',
}

const pathKeys = [
  'brandAccountsList',
  'coreMembersList',
  'usersDownload',
  'aiUsageExport',
] as const satisfies PathKeyBase[]
type PathKey = (typeof pathKeys)[number]

const pathMap: Record<PathGroupKey, PathKey[]> = {
  settings: [
    'brandAccountsList',
    'coreMembersList',
    'usersDownload',
    'aiUsageExport',
  ],
}

export const useNavigationGroups = () => {
  const { t } = useTranslation()
  const matchedPathKey = useMatchedPathKey(pathKeys)

  const createLabeledPath = React.useMemo(
    () => createLabeledPathWithI18n(t),
    [t],
  )

  const createNavigationGroupItem = React.useCallback(
    (pathKey: PathKey): NavigationGroupItem => {
      const labeledPath = createLabeledPath(pathKey, {})

      return { ...labeledPath, isActive: pathKey === matchedPathKey }
    },
    [matchedPathKey, createLabeledPath],
  )

  return React.useMemo<NavigationGroup<PathGroupKey>[]>(
    () =>
      pathGroupKeys.map((pathGroupKey) => ({
        groupKey: pathGroupKey,
        icon: iconMap[pathGroupKey],
        isInitiallyExpanded: true,
        items: pathMap[pathGroupKey].map(createNavigationGroupItem),
        label: t(pathGroupLabelI18nKeyMap[pathGroupKey]),
      })),
    [createNavigationGroupItem, t],
  )
}
